import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

function SEO({ 
  title,           
  description,    
  keywords,   
  image,   
  slug,
  type = "article", 
  geoRegion,      
  geoPlace,       
  geoIcbm,
  authorName = "EuroWineTrip",
  datePublished,
  dateModified,
  aiSummary, 
}) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language; 
  const siteBaseUrl = "https://www.eurowinetrip.com"; 
  
  // Bing & Baidu Optimization: Dynamic titles based on language
  const displayTitle = title 
    ? `${title} - EuroWineTrip` 
    : (currentLang === 'zh' ? "欧洲葡萄酒之旅 EuroWineTrip" : "EuroWineTrip - European Wine Culture");

  const defaultDesc = currentLang === 'zh' 
    ? "专业欧洲葡萄酒文化博览，深度解析顶级酒庄与风土人情。" 
    : "Professional European wine blog, deeply analyzing top wineries and terroir.";
    
  const displayDesc = aiSummary || description || defaultDesc;
  
  // Clean URL structure - same URL for both languages
  const path = slug ? `/${slug.replace(/^\//, '')}` : "";
  const canonicalUrl = `${siteBaseUrl}${path}`;
  
  // UPDATED: Use wine-themed default image for posts, not logo
  const absoluteImage = image?.startsWith('http') 
    ? image 
    : `${siteBaseUrl}${image || '/assets/covers/default.webp'}`;

  // Structured Data for Bing/Google/Baidu
  const structuredData = {
    "@context": "https://schema.org",
    "@type": type === "article" ? "BlogPosting" : 
             type === "collection" ? "CollectionPage" : "WebSite",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "headline": displayTitle,
    "description": displayDesc,
    "image": absoluteImage,
    "inLanguage": currentLang === 'zh' ? "zh-CN" : "en-US",
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": "EuroWineTrip",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteBaseUrl}/logo.png` // KEEP LOGO here for brand identity
      }
    }
  };

  return (
    <Helmet>
      {/* 1. Language - Bing uses this to determine index sorting */}
      <html lang={currentLang === 'zh' ? 'zh-CN' : 'en-US'} />
      <title>{displayTitle}</title>
      
      {/* 2. Primary Meta Tags */}
      <meta name="description" content={displayDesc} />
      <meta name="keywords" content={keywords || (currentLang === 'zh' ? "葡萄酒, 欧洲酒庄, 品鉴指南" : "Wine, European Wineries, Tasting Guide")} />
      
      {/* Bing specific behavior: helps verify site ownership if not in index.html */}
      <meta name="author" content={authorName} />

      {/* 3. Canonical & Device Support - CLEAN URL without parameters */}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="applicable-device" content="pc,mobile" />
      
      {/* 4. Geographic SEO - Important for Baidu */}
      <meta name="geo.region" content={geoRegion || "CN"} />
      <meta name="geo.placename" content={geoPlace || "Shanghai"} />

      {/* 5. Open Graph - Bing uses these for rich search snippets */}
      <meta property="og:type" content={type === "article" ? "article" : "website"} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={displayTitle} />
      <meta property="og:description" content={displayDesc} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:locale" content={currentLang === 'zh' ? 'zh_CN' : 'en_US'} />

      {/* 6. Language Alternates (Hreflang) - CRITICAL: Both languages point to same URL */}
      <link rel="alternate" hreflang="zh-Hans" href={canonicalUrl} />
      <link rel="alternate" hreflang="en" href={canonicalUrl} />
      <link rel="alternate" hreflang="x-default" href={canonicalUrl} />

      {/* 7. JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

export default SEO;